import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  ActiveDefectLayerId,
  layerRemoved,
  layersRemovedByRecipeId,
} from './activeLayersSlice';

export type SurveyDataManagerModal = 'addLayer' | 'visualizationMeasure';

export type LayerActivationStatus = 'idle' | 'pending' | 'error';

export interface LayerActivationState {
  readonly status: LayerActivationStatus;
  readonly kind: 'wizard' | 'recipe' | null;
  readonly recipeId: string | null;
  readonly message: string | null;
}

export interface SurveyDataManagerUiState {
  activeModal: SurveyDataManagerModal | null;

  selectedLayerId: ActiveDefectLayerId | null;
  expandedLayerId: ActiveDefectLayerId | null;
  advancedSettingsLayerId: ActiveDefectLayerId | null;
  pendingMapLayerIds: ActiveDefectLayerId[];
  layerActivation: LayerActivationState;
}

const idleActivation: LayerActivationState = {
  status: 'idle',
  kind: null,
  recipeId: null,
  message: null,
};

const initialState: SurveyDataManagerUiState = {
  activeModal: null,
  selectedLayerId: null,
  expandedLayerId: null,
  advancedSettingsLayerId: null,
  pendingMapLayerIds: [],
  layerActivation: idleActivation,
};

function addPendingMapLayerId(
  pendingIds: ActiveDefectLayerId[],
  layerId: ActiveDefectLayerId,
): ActiveDefectLayerId[] {
  if (pendingIds.includes(layerId)) {
    return pendingIds;
  }
  pendingIds.push(layerId);
  return pendingIds;
}

function removePendingMapLayerId(
  pendingIds: ActiveDefectLayerId[],
  layerId: ActiveDefectLayerId,
): void {
  const index = pendingIds.indexOf(layerId);
  if (index >= 0) {
    pendingIds.splice(index, 1);
  }
}

const uiSlice = createSlice({
  name: 'surveyDataManagerUi',
  initialState,
  reducers: {
    modalOpened(state, action: PayloadAction<SurveyDataManagerModal>) {
      state.activeModal = action.payload;
    },
    modalClosed(state) {
      state.activeModal = null;
    },
    layerSelected(state, action: PayloadAction<ActiveDefectLayerId | null>) {
      state.selectedLayerId = action.payload;
    },
    layerSettingsToggled(state, action: PayloadAction<ActiveDefectLayerId>) {
      state.expandedLayerId =
        state.expandedLayerId === action.payload ? null : action.payload;
    },
    layerSettingsClosed(state) {
      state.expandedLayerId = null;
      state.advancedSettingsLayerId = null;
    },
    advancedSettingsToggled(state, action: PayloadAction<ActiveDefectLayerId>) {
      state.advancedSettingsLayerId =
        state.advancedSettingsLayerId === action.payload ? null : action.payload;
    },
    layerUiIdRemapped(
      state,
      action: PayloadAction<{
        readonly fromId: ActiveDefectLayerId;
        readonly toId: ActiveDefectLayerId;
      }>,
    ) {
      if (state.expandedLayerId === action.payload.fromId) {
        state.expandedLayerId = action.payload.toId;
      }
      if (state.advancedSettingsLayerId === action.payload.fromId) {
        state.advancedSettingsLayerId = action.payload.toId;
      }
      if (state.selectedLayerId === action.payload.fromId) {
        state.selectedLayerId = action.payload.toId;
      }
      const pendingIndex = state.pendingMapLayerIds.indexOf(action.payload.fromId);
      if (pendingIndex >= 0) {
        state.pendingMapLayerIds[pendingIndex] = action.payload.toId;
      }
    },
    layerMapRenderPending(state, action: PayloadAction<ActiveDefectLayerId>) {
      addPendingMapLayerId(state.pendingMapLayerIds, action.payload);
    },
    layerMapRenderSettled(state, action: PayloadAction<ActiveDefectLayerId>) {
      removePendingMapLayerId(state.pendingMapLayerIds, action.payload);
    },
    layerActivationStarted(
      state,
      action: PayloadAction<{
        readonly kind: 'wizard' | 'recipe';
        readonly recipeId?: string | null;
      }>,
    ) {
      state.layerActivation = {
        status: 'pending',
        kind: action.payload.kind,
        recipeId: action.payload.recipeId ?? null,
        message: null,
      };
    },
    layerActivationSucceeded(state) {
      state.layerActivation = idleActivation;
    },
    layerActivationFailed(
      state,
      action: PayloadAction<{ readonly message: string }>,
    ) {
      state.layerActivation = {
        ...state.layerActivation,
        status: 'error',
        message: action.payload.message,
      };
    },
    layerActivationErrorCleared(state) {
      if (state.layerActivation.status === 'error') {
        state.layerActivation = idleActivation;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(layerRemoved, (state, action) => {
      removePendingMapLayerId(state.pendingMapLayerIds, action.payload.id);
    });
    builder.addCase(layersRemovedByRecipeId, (state, action) => {
      const matchingIds = action.payload.matchingLayerIds;
      if (matchingIds == null || matchingIds.length === 0) {
        return;
      }
      const removed = new Set(matchingIds);
      state.pendingMapLayerIds = state.pendingMapLayerIds.filter(
        (id) => !removed.has(id),
      );
    });
  },
});

export const {
  modalOpened,
  modalClosed,
  layerSelected,
  layerSettingsToggled,
  layerSettingsClosed,
  advancedSettingsToggled,
  layerUiIdRemapped,
  layerMapRenderPending,
  layerMapRenderSettled,
  layerActivationStarted,
  layerActivationSucceeded,
  layerActivationFailed,
  layerActivationErrorCleared,
} = uiSlice.actions;
export const surveyDataManagerUiReducer = uiSlice.reducer;
export default uiSlice.reducer;
